import { Decision, DecisionType, RiskLevel, Evidence } from '../types/audit-trail';

// Helper function to generate realistic dates
const getRandomDate = (daysAgo: number): Date => {
  const now = new Date();
  const randomDays = Math.floor(Math.random() * daysAgo);
  const randomHours = Math.floor(Math.random() * 24);
  const randomMinutes = Math.floor(Math.random() * 60);
  
  const date = new Date(now);
  date.setDate(date.getDate() - randomDays);
  date.setHours(randomHours, randomMinutes, 0, 0);
  
  return date;
};

// Helper function to generate signature hash
const generateSignature = (id: string): string => {
  return `sha256:${id.slice(0, 16)}${Math.random().toString(36).substr(2, 48)}`;
};

// Sample humanitarian decision data
export const sampleDecisions: Decision[] = [
  {
    id: 'dec_2025_001',
    createdAt: new Date('2025-06-19T13:46:00Z'),
    updatedAt: new Date('2025-06-19T13:46:00Z'),
    createdBy: 'user_sarah_johnson',
    createdByName: 'Sarah Johnson',
    status: 'draft',
    syncStatus: 'pending',
    
    title: 'Emergency Resource Allocation for Rohingya Crisis',
    type: 'resource_allocation',
    decisionMade: 'Allocate 5,000 emergency shelter kits and 20 metric tons of high-energy biscuits to Cox\'s Bazar refugee camps in response to monsoon flooding predictions.',
    rationale: 'Satellite imagery and meteorological data indicate a 78% probability of severe flooding within 48-72 hours. Current shelter conditions are inadequate for 45,000 refugees in the affected zones. Pre-positioning resources now will enable rapid response and prevent humanitarian catastrophe.',
    riskLevel: 'high',
    
    informationSources: [
      'Sentinel-2 Satellite Imagery',
      'Bangladesh Meteorological Department',
      'UNHCR Field Reports',
      'Community Feedback Mechanisms',
      'Partner Organization Assessments'
    ],
    stakeholders: [
      'UNHCR Protection Officer',
      'WFP Logistics Coordinator',
      'Camp Management Committee',
      'Local Government Representatives',
      'Refugee Community Leaders'
    ],
    alternatives: 'Alternative approaches included: 1) Wait for confirmed flooding before deployment (rejected due to response time constraints), 2) Smaller pre-positioning of 2,000 kits (rejected as insufficient for population at risk), 3) Cash-based assistance instead of in-kind (rejected due to market limitations during emergencies)',
    
    location: {
      lat: 21.1793,
      lng: 92.2771,
      address: 'Cox\'s Bazar, Chittagong Division, Bangladesh',
      accuracy: 95
    },
    
    evidence: [
      {
        id: 'evd_001',
        type: 'document',
        filename: 'flood_risk_assessment_june2025.pdf',
        size: 2456789,
        mimeType: 'application/pdf',
        uploadStatus: 'complete',
        uploadedAt: new Date('2025-06-19T13:30:00Z'),
        metadata: { pages: 12 }
      },
      {
        id: 'evd_002',
        type: 'photo',
        filename: 'camp_conditions_aerial.jpg',
        size: 1234567,
        mimeType: 'image/jpeg',
        uploadStatus: 'complete',
        uploadedAt: new Date('2025-06-19T13:35:00Z'),
        metadata: { dimensions: { width: 3840, height: 2160 } }
      }
    ],
    
    signature: generateSignature('dec_2025_001'),
    deviceId: 'device_field_tablet_03',
    editHistory: [],
    
    tags: ['emergency-response', 'monsoon-2025', 'rohingya', 'shelter', 'food-security'],
    priority: 'urgent',
    
    reviewedBy: 'Country Director',
    reviewedAt: new Date('2025-06-19T14:15:00Z'),
    reviewNotes: 'Approved for immediate implementation. Coordinate with government counterparts.',
    approvalStatus: 'approved'
  },

  {
    id: 'dec_2025_002',
    createdAt: getRandomDate(7),
    updatedAt: getRandomDate(7),
    createdBy: 'user_ahmed_hassan',
    createdByName: 'Ahmed Hassan',
    status: 'synced',
    syncStatus: 'synced',
    
    title: 'Cholera Outbreak Response - Dadaab Complex',
    type: 'security_response',
    decisionMade: 'Deploy emergency medical team with cholera treatment capacity and establish isolation units in Dagahaley camp within 24 hours.',
    rationale: 'Laboratory confirmation of 15 cholera cases with rapid increase in acute watery diarrhea cases (40% increase in 72 hours). Water quality tests show contamination in Block C-7. Immediate medical intervention required to prevent epidemic spread.',
    riskLevel: 'high',
    
    informationSources: [
      'Laboratory Test Results',
      'WHO Disease Surveillance Reports',
      'Water Quality Testing',
      'Community Health Workers',
      'Médecins Sans Frontières Reports'
    ],
    stakeholders: [
      'WHO Health Cluster Coordinator',
      'MSF Medical Coordinator',
      'UNHCR Public Health Officer',
      'Kenyan Ministry of Health',
      'Camp Health Committee'
    ],
    alternatives: 'Considered referral to external hospitals (rejected due to capacity limitations and transport risks) and treatment in existing health facilities (rejected due to infection control requirements)',
    
    location: {
      lat: 0.2063,
      lng: 40.3142,
      address: 'Dadaab Refugee Complex, Garissa County, Kenya'
    },
    
    evidence: [
      {
        id: 'evd_003',
        type: 'document',
        filename: 'cholera_lab_results.pdf',
        size: 856432,
        mimeType: 'application/pdf',
        uploadStatus: 'complete',
        metadata: { pages: 3 }
      }
    ],
    
    signature: generateSignature('dec_2025_002'),
    deviceId: 'device_health_mobile_01',
    editHistory: [],
    
    tags: ['health-emergency', 'cholera', 'outbreak-response', 'dadaab'],
    priority: 'urgent'
  },

  {
    id: 'dec_2025_003',
    createdAt: getRandomDate(14),
    updatedAt: getRandomDate(12),
    createdBy: 'user_maria_gonzalez',
    createdByName: 'Maria Gonzalez',
    status: 'synced',
    syncStatus: 'synced',
    
    title: 'Food Distribution Modification - Tigray Region',
    type: 'program_modification',
    decisionMade: 'Suspend general food distribution in Axum and Adwa towns due to active hostilities. Implement emergency cash transfer program for 12,000 affected households.',
    rationale: 'Security incidents reported along distribution routes with targeting of humanitarian convoys. Local markets remain functional with adequate food stocks. Cash transfers provide safer alternative while maintaining purchasing power for affected populations.',
    riskLevel: 'medium',
    
    informationSources: [
      'Security Incident Reports',
      'Market Assessment Data',
      'Partner Security Briefings',
      'Community Focal Points',
      'Government Security Updates'
    ],
    stakeholders: [
      'WFP Programme Manager',
      'Security Risk Management',
      'Local Partner Organizations',
      'Tigray Interim Administration',
      'Beneficiary Representatives'
    ],
    alternatives: 'Considered armed escort for distributions (rejected due to access restrictions) and suspension without alternative assistance (rejected due to humanitarian imperative)',
    
    location: {
      lat: 14.1205,
      lng: 38.7216,
      address: 'Axum, Tigray Region, Ethiopia'
    },
    
    evidence: [],
    
    signature: generateSignature('dec_2025_003'),
    deviceId: 'device_program_laptop_02',
    editHistory: [
      {
        id: 'edit_001',
        editedAt: getRandomDate(12),
        editedBy: 'user_maria_gonzalez',
        editedByName: 'Maria Gonzalez',
        field: 'decisionMade',
        oldValue: 'Suspend general food distribution in Axum and Adwa towns due to active hostilities.',
        newValue: 'Suspend general food distribution in Axum and Adwa towns due to active hostilities. Implement emergency cash transfer program for 12,000 affected households.',
        reason: 'Added cash transfer alternative after consultation with finance team'
      }
    ],
    
    tags: ['food-security', 'cash-transfer', 'tigray', 'conflict-response'],
    priority: 'high'
  },

  {
    id: 'dec_2025_004',
    createdAt: getRandomDate(21),
    updatedAt: getRandomDate(21),
    createdBy: 'user_james_okonkwo',
    createdByName: 'James Okonkwo',
    status: 'synced',
    syncStatus: 'synced',
    
    title: 'Child Protection Case Management Protocol',
    type: 'program_modification',
    decisionMade: 'Establish specialized case management protocol for unaccompanied minors in South Sudanese refugee camps, with mandatory family tracing within 72 hours of identification.',
    rationale: 'Significant increase in unaccompanied minors (127 cases in May) due to renewed displacement. Existing protocols insufficient for timely family reunification. New protocol ensures child protection standards while addressing specific vulnerabilities.',
    riskLevel: 'medium',
    
    informationSources: [
      'Child Protection Monitoring Data',
      'UNICEF Guidelines',
      'Community Based Protection Reports',
      'Inter-Agency Child Protection Working Group',
      'Family Tracing Network Reports'
    ],
    stakeholders: [
      'Child Protection Officer',
      'UNICEF Representative',
      'Save the Children Program Manager',
      'Community Protection Committees',
      'Youth Representatives'
    ],
    
    location: {
      lat: 3.2058,
      lng: 31.6847,
      address: 'Maban County, Upper Nile State, South Sudan'
    },
    
    evidence: [
      {
        id: 'evd_004',
        type: 'document',
        filename: 'child_protection_statistics_may2025.xlsx',
        size: 445678,
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        uploadStatus: 'complete'
      }
    ],
    
    signature: generateSignature('dec_2025_004'),
    deviceId: 'device_protection_tablet_01',
    editHistory: [],
    
    tags: ['child-protection', 'unaccompanied-minors', 'family-tracing', 'south-sudan'],
    priority: 'high'
  },

  {
    id: 'dec_2025_005',
    createdAt: getRandomDate(3),
    updatedAt: getRandomDate(3),
    createdBy: 'user_fatima_al_zahra',
    createdByName: 'Fatima Al-Zahra',
    status: 'submitted',
    syncStatus: 'synced',
    
    title: 'Emergency Water Trucking - Yemen Crisis',
    type: 'resource_allocation',
    decisionMade: 'Deploy emergency water trucking to provide 15 liters per person per day to 8,500 IDPs in Al Hudaydah informal settlements for immediate 30-day period.',
    rationale: 'Critical infrastructure damage from recent hostilities resulted in complete water system failure. Current access below 3L/person/day creating acute health risks. Trucking provides immediate life-saving intervention while infrastructure repairs planned.',
    riskLevel: 'high',
    
    informationSources: [
      'Rapid WASH Assessment',
      'Infrastructure Damage Reports',
      'Health Monitoring Data',
      'IDP Community Leaders',
      'Local Water Authority'
    ],
    stakeholders: [
      'WASH Cluster Coordinator',
      'OXFAM WASH Manager',
      'Local Water Corporation',
      'IDP Camp Management',
      'Community Water Committees'
    ],
    
    location: {
      lat: 14.7931,
      lng: 42.9511,
      address: 'Al Hudaydah, Yemen'
    },
    
    evidence: [
      {
        id: 'evd_005',
        type: 'photo',
        filename: 'water_damage_assessment.jpg',
        size: 2156789,
        mimeType: 'image/jpeg',
        uploadStatus: 'pending'
      }
    ],
    
    signature: generateSignature('dec_2025_005'),
    deviceId: 'device_wash_mobile_04',
    editHistory: [],
    
    tags: ['wash-emergency', 'water-trucking', 'yemen-crisis', 'idp-response'],
    priority: 'urgent',
    
    approvalStatus: 'pending'
  },

  {
    id: 'dec_2025_006',
    createdAt: getRandomDate(45),
    updatedAt: getRandomDate(40),
    createdBy: 'user_patricia_mueller',
    createdByName: 'Patricia Mueller',
    status: 'archived',
    syncStatus: 'synced',
    
    title: 'Partner Capacity Assessment - Local NGOs',
    type: 'partner_management',
    decisionMade: 'Approve three local NGO partners (Hope Foundation, Community Outreach Network, Women\'s Development Alliance) for sub-grant implementation of livelihood programs, with enhanced monitoring framework.',
    rationale: 'Capacity assessments demonstrate technical competency and community acceptance. Local implementation increases program reach and cultural appropriateness while reducing operational costs by 23%. Enhanced monitoring addresses fiduciary risk concerns.',
    riskLevel: 'low',
    
    informationSources: [
      'Partner Capacity Assessment Reports',
      'Reference Checks',
      'Community Consultation Results',
      'Financial Audit Reports',
      'Due Diligence Reviews'
    ],
    stakeholders: [
      'Partnership Manager',
      'Programme Quality Assurance',
      'Finance Officer',
      'Community Representatives',
      'Local Government'
    ],
    
    location: {
      lat: -6.1659,
      lng: 106.8639,
      address: 'Jakarta, Indonesia'
    },
    
    evidence: [],
    
    signature: generateSignature('dec_2025_006'),
    deviceId: 'device_partnership_desk_01',
    editHistory: [],
    
    tags: ['partnership', 'capacity-building', 'local-ngos', 'livelihood'],
    priority: 'normal'
  },

  {
    id: 'dec_2025_007',
    createdAt: getRandomDate(8),
    updatedAt: getRandomDate(6),
    createdBy: 'user_carlos_mendez',
    createdByName: 'Carlos Mendez',
    status: 'synced',
    syncStatus: 'synced',
    
    title: 'Hurricane Preparedness - Caribbean Region',
    type: 'security_response',
    decisionMade: 'Activate regional emergency preparedness plan with pre-positioning of emergency supplies in Dominican Republic and Jamaica. Deploy rapid response teams to standby positions.',
    rationale: 'Meteorological models show 85% probability of Category 4 hurricane impacting Hispaniola and Jamaica within 72 hours. Historical data indicates potential for 150,000+ affected population. Proactive deployment reduces response time from 5 days to 18 hours.',
    riskLevel: 'high',
    
    informationSources: [
      'National Hurricane Center Bulletins',
      'Regional Meteorological Services',
      'CEDMA Early Warning Systems',
      'Government Emergency Management',
      'Caribbean Disaster Emergency Management Agency'
    ],
    stakeholders: [
      'Regional Emergency Coordinator',
      'National Disaster Management Offices',
      'Caribbean Community (CARICOM)',
      'UN Disaster Assessment Team',
      'Regional Security System'
    ],
    
    location: {
      lat: 18.7357,
      lng: -70.1627,
      address: 'Santo Domingo, Dominican Republic'
    },
    
    evidence: [
      {
        id: 'evd_007',
        type: 'document',
        filename: 'hurricane_forecast_models.pdf',
        size: 3456789,
        mimeType: 'application/pdf',
        uploadStatus: 'complete',
        metadata: { pages: 8 }
      }
    ],
    
    signature: generateSignature('dec_2025_007'),
    deviceId: 'device_regional_hq_01',
    editHistory: [],
    
    tags: ['hurricane-preparedness', 'caribbean', 'early-warning', 'emergency-response'],
    priority: 'urgent'
  },

  {
    id: 'dec_2025_008',
    createdAt: getRandomDate(2),
    updatedAt: getRandomDate(1),
    createdBy: 'user_aisha_kone',
    createdByName: 'Aisha Koné',
    status: 'draft',
    syncStatus: 'pending',
    
    title: 'Education Continuity Plan - Mali Conflict Zones',
    type: 'program_modification',
    decisionMade: 'Implement mobile education units and radio-based learning programs to serve 3,200 conflict-affected children in northern Mali regions where formal schools remain closed.',
    rationale: 'Security assessment confirms 47 schools remain non-functional due to conflict. Children out of school for 8+ months facing protection risks and loss of educational opportunities. Mobile units provide flexible, safe learning environment while radio programs ensure broader reach.',
    riskLevel: 'medium',
    
    informationSources: [
      'Education Cluster Assessments',
      'Security Incident Mapping',
      'Child Protection Monitoring',
      'Parent-Teacher Association Consultations',
      'Ministry of Education Guidelines'
    ],
    stakeholders: [
      'Education Cluster Coordinator',
      'UNICEF Education Officer',
      'Parent-Teacher Associations',
      'Community Education Committees',
      'Traditional Leaders'
    ],
    
    location: {
      lat: 16.7666,
      lng: -3.0026,
      address: 'Timbuktu Region, Mali'
    },
    
    evidence: [
      {
        id: 'evd_008',
        type: 'document',
        filename: 'education_needs_assessment.pdf',
        size: 1876543,
        mimeType: 'application/pdf',
        uploadStatus: 'failed'
      }
    ],
    
    signature: generateSignature('dec_2025_008'),
    deviceId: 'device_education_tablet_02',
    editHistory: [],
    
    tags: ['education-emergency', 'conflict-response', 'mali', 'mobile-education'],
    priority: 'high',
    
    approvalStatus: 'pending'
  }
];

// Function to calculate dashboard statistics
export const calculateDashboardStats = (decisions: Decision[]) => {
  const total = decisions.length;
  const riskLevels = { low: 0, medium: 0, high: 0 };
  const statuses = { draft: 0, submitted: 0, synced: 0, archived: 0 };
  const types = {
    security_response: 0,
    resource_allocation: 0,
    beneficiary_selection: 0,
    program_modification: 0,
    partner_management: 0,
    other: 0
  };
  
  const now = new Date();
  const last24h = decisions.filter(d => 
    now.getTime() - new Date(d.createdAt).getTime() <= 24 * 60 * 60 * 1000
  ).length;
  
  const last7days = decisions.filter(d => 
    now.getTime() - new Date(d.createdAt).getTime() <= 7 * 24 * 60 * 60 * 1000
  ).length;
  
  const pendingSync = decisions.filter(d => d.syncStatus === 'pending' || d.syncStatus === 'failed').length;
  const highRiskUrgent = decisions.filter(d => d.riskLevel === 'high' && d.priority === 'urgent').length;
  
  decisions.forEach(decision => {
    riskLevels[decision.riskLevel]++;
    statuses[decision.status]++;
    types[decision.type]++;
  });
  
  return {
    total,
    riskLevels,
    statuses,
    types,
    recentActivity: {
      last24h,
      last7days,
      pendingSync,
      highRiskUrgent
    }
  };
};